import styled from "@emotion/styled";
import theme from "@utils/theme";

export const DashboardWrapper = styled.div`
  padding: 18px;
  background: #fafbfc;
  min-height: 100vh;
  font-family:
    "Inter",
    -apple-system,
    BlinkMacSystemFont,
    sans-serif;
  @media (max-width: 800px) {
    padding: 0px;
  }
`;

export const HeaderArea = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
`;

export const HeaderText = styled.div`
  h1 {
    font-size: 20px;
    font-weight: 600;
    color: #111827;
    margin: 0 0 4px 0;
  }
  p {
    font-size: 13px;
    color: #6b7280;
    margin: 0;
    font-weight: 400;
  }
`;

export const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 24px;
`;

export const MetricCard = styled.div`
  background: white;
  padding: 20px;
  border-radius: 12px;
  border: 1px solid #f3f4f6;
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    border-color: ${theme.orange.orange200};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    transform: translateY(-1px);
  }
`;

export const MetricHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

export const MetricIcon = styled.div<any>`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: ${(props) => props.background};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => props.color};
`;

export const MetricTrend = styled.div<any>`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 500;
  color: ${(props: any) => (props.positive ? "#059669" : "#dc2626")};
`;

export const MetricValue = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 2px;
`;

export const MetricLabel = styled.div`
  font-size: 12px;
  color: #6b7280;
  font-weight: 400;
`;

export const MainDashboard = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 20px;
  margin-bottom: 20px;
  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`;

export const ContentCard = styled.div`
  background: white;
  padding: 20px;
  border-radius: 12px;
  border: 1px solid #f3f4f6;
`;

export const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

export const CardTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  margin: 0;
`;

export const ListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const ListItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #fafbfc;
  border-radius: 8px;
`;

export const ListIcon = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: ${theme.orange.orange50};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.orange.orange500};
  flex-shrink: 0;
`;

export const ListContent = styled.div`
  flex: 1;
`;

export const ListMainText = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: #374151;
`;

export const ListSubText = styled.div`
  font-size: 11px;
  color: #9ca3af;
`;

export const ListBadge = styled.div<any>`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  background: ${(props) => props.background || "#f3f4f6"};
  color: ${(props) => props.color || "#6b7280"};
`;

export const BottomSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`;

export const OverviewList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const OverviewItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid #f3f4f6;

  &:last-child {
    border-bottom: none;
  }
`;

export const OverviewLabel = styled.div`
  font-size: 12px;
  color: #6b7280;
`;

export const OverviewValue = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #111827;
`;

export const EmptyState = styled.div`
  padding: 40px;
  text-align: center;
  color: #9ca3af;
  font-size: 13px;
`;

export const ChartSection = styled.div`
  margin-bottom: 24px;
`;

export const ChartToggleContainer = styled.div`
  display: flex;
  background: #f3f4f6;
  padding: 4px;
  border-radius: 8px;
  gap: 4px;
`;

export const ChartToggleButton = styled.button<{ active: boolean }>`
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  transition: all 0.2s;
  background: ${(props) => (props.active ? "white" : "transparent")};
  color: ${(props) => (props.active ? theme.orange.orange500 : "#6b7280")};
  box-shadow: ${(props) => (props.active ? "0 1px 2px rgba(0,0,0,0.05)" : "none")};
  outline: none;
  
  &:hover {
    color: ${theme.orange.orange500};
  }
`;

export const ChartControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const ChartSummaryRow = styled.div`
  display: flex;
  gap: 24px;
  margin-bottom: 16px;
  padding: 12px 16px;
  background: #fafbfc;
  border-radius: 8px;
`;

export const ChartSummaryItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;

  span:first-of-type {
    font-size: 11px;
    color: #6b7280;
    font-weight: 400;
  }

  span:last-of-type {
    font-size: 14px;
    font-weight: 600;
    color: #111827;
  }
`;

